import React, { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTickets } from '../../contexts/TicketContext';
import { useProject } from '../../contexts/ProjectContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFontSize } from '../../contexts/FontContext';
import { useTranslation } from '../../translations';
import KPICard from '../../components/dashboard/KPICard';
import TaskList from '../../components/dashboard/TaskList';
import AllProjectsTasks from '../../components/dashboard/AllProjectsTasks';
import {
  TicketIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const StaffDashboard = () => {
  const { user } = useAuth();
  const { tickets, loading } = useTickets();
  const { selectedProject, projects } = useProject();
  const { language } = useLanguage();
  const { getFontClass } = useFontSize();
  const { t } = useTranslation(language);

  // Dashboard stats - gerçek ticket verilerinden hesapla
  const dashboardStats = useMemo(() => {
    // Tickets array'i boş veya undefined ise güvenli değerler döndür
    const safeTickets = Array.isArray(tickets) ? tickets : [];

    // Status helper fonksiyonu - hem string hem object durumlarını handle eder
    const getStatusName = (ticket) => {
      if (!ticket) return null;
      // Status object ise status_name'i al
      if (typeof ticket.status === 'object' && ticket.status?.status_name) {
        return ticket.status.status_name;
      }
      // Status string ise direkt kullan
      if (typeof ticket.status === 'string') {
        return ticket.status;
      }
      return null;
    };

    // Priority helper fonksiyonu
    const getPriorityName = (ticket) => {
      if (!ticket) return 'Medium';
      if (typeof ticket.priority === 'object' && ticket.priority?.priority_name) {
        return ticket.priority.priority_name;
      }
      if (typeof ticket.priority === 'string') {
        return ticket.priority;
      }
      return 'Medium';
    };

    // Tamamlanan ticket'ları filtrele (Bitti status'üne sahip)
    const completedTickets = safeTickets.filter(ticket => {
      const status = getStatusName(ticket);
      return status === 'Bitti' || status === 'Done';
    });

    // En son tamamlanan ticket'ları al (updated_at veya created_at'e göre sırala)
    const recentCompleted = completedTickets
      .sort((a, b) => {
        const dateA = new Date(a.updated_at || a.created_at || 0);
        const dateB = new Date(b.updated_at || b.created_at || 0);
        return dateB - dateA; // En yeni önce
      })
      .slice(0, 5) // En son 5 tanesini al
      .map(ticket => ({
        id: ticket.id || 'N/A',
        title: ticket.title || 'Başlıksız Ticket',
        status: getStatusName(ticket) || 'Bitti',
        priority: getPriorityName(ticket)
      }))
      .filter(task => task.title && task.id && task.id !== 'N/A');

    // Bugün tamamlanan ticket sayısı
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayCompleted = completedTickets.filter(ticket => {
      const updatedDate = new Date(ticket.updated_at || ticket.created_at || 0);
      updatedDate.setHours(0, 0, 0, 0);
      return updatedDate.getTime() === today.getTime();
    }).length;

    return {
      myOpenTickets: safeTickets.filter(t => {
        const status = getStatusName(t);
        return status === 'Beklemede' || status === 'Yapılıyor' || status === 'To Do' || status === 'In Progress';
      }).length,
      todayCompleted: todayCompleted,
      pendingTasks: safeTickets.filter(t => {
        const status = getStatusName(t);
        return status === 'Beklemede' || status === 'To Do';
      }).length,
      urgentTickets: safeTickets.filter(t => {
        const priority = getPriorityName(t);
        return priority === 'High' || priority === 'Urgent' || priority === 'Yüksek' || priority === 'Acil';
      }).length,
      myTasksList: safeTickets
        .filter(t => {
          const status = getStatusName(t);
          return status === 'Beklemede' || status === 'Yapılıyor' || status === 'To Do' || status === 'In Progress';
        })
        .slice(0, 5)
        .map(ticket => ({
          id: ticket.id || 'N/A',
          title: ticket.title || 'Başlıksız Ticket',
          status: getStatusName(ticket) || 'Bilinmiyor',
          priority: getPriorityName(ticket)
        }))
        .filter(task => task.title && task.id && task.id !== 'N/A'),
      completedTasksList: recentCompleted.length > 0 ? recentCompleted : [
        // Eğer tamamlanan ticket yoksa boş mesaj gösterilecek
      ]
    };
  }, [tickets]);


  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-2xl font-bold text-text-primary dark:text-dark-text-primary mb-1">
            {selectedProject ? selectedProject.project_name : t('staffDashboard')}
          </h2>
          <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
            {selectedProject ? `${selectedProject.project_name} ${t('projectTasksDesc')}` : t('dailyTasksDesc')}
          </p>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highlight-500"></div>
          <span className="ml-3 text-text-secondary dark:text-dark-text-secondary">{t('dashboardLoading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Page Title */}
      <div>
        <h2 className="text-2xl font-bold text-text-primary dark:text-dark-text-primary mb-1">
          {selectedProject ? selectedProject.project_name : t('staffDashboard')}
        </h2>
        <p className="text-sm text-text-secondary dark:text-dark-text-secondary">
          {selectedProject ? `${selectedProject.project_name} ${t('projectTasksDesc')}` : t('dailyTasksDesc')}
        </p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={t('myOpenTickets')}
          value={dashboardStats.myOpenTickets.toString()}
          icon={TicketIcon}
          color="primary"
        />
        <KPICard
          title={t('todayCompleted')}
          value={dashboardStats.todayCompleted.toString()}
          icon={CheckCircleIcon}
          color="success"
        />
        <KPICard
          title={t('pendingTasks')}
          value={dashboardStats.pendingTasks.toString()}
          icon={ClockIcon}
          color="warning"
        />
        <KPICard
          title={t('urgentTickets')}
          value={dashboardStats.urgentTickets.toString()}
          icon={ExclamationTriangleIcon}
          color="error"
        />
      </div>

      {/* Task Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TaskList
          title={t('myAssignedTasks')}
          tasks={dashboardStats.myTasksList}
        />
        <TaskList
          title={t('recentCompletedTasks')}
          tasks={dashboardStats.completedTasksList}
        />
      </div>

      {/* Tüm Projelerden Görevler Component */}
      <AllProjectsTasks user={user} projects={projects} />
    </div>
  );
};

export default StaffDashboard;